import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { apiSignOut } from 'src/api/account/organizer';
import { apiAccountSignin, apiAccountSignup, apiLoadUserModel } from 'src/api/account/profile';
import { SignInRequestType } from 'src/models/Account/Signin';
import { SignUpRequestType } from 'src/models/Account/Signup';
import { Enums } from 'src/models/enums';
import { signInOutSelector } from 'src/pages/Account/Profile/Signin/store/selectors';
import { signInOutSlice } from 'src/pages/Account/Profile/Signin/store/slice';
import { ROUTE_CUSTOMER_TYPE, ROUTE_MY_ACCOUNT_PAGE, ROUTE_SIGNIN_PATH } from 'src/routes';
import { commonSlice } from 'src/store/commonPart/slice';
import { httpResponseCheckSaga } from 'src/store/commonSaga';
import { currentUserSlice } from 'src/store/user/slice';
import { anonymizeFullstoryIdentity } from 'src/utils/fullstory';
import { safeRedirect } from 'src/utils/hardRedirect';
import { setLocalStorage } from 'src/utils/localStorage';
import { trackPartnerEvent } from 'src/utils/trackPartnerEvent';
import { PromiseReturnType } from 'src/utils/types';
import { getSearchReturnUrl } from 'src/utils/url';

function* signInSaga({ payload }: PayloadAction<SignInRequestType>): Generator {
  const { initialEmail, from, ...requestData } = payload;

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiAccountSignin, requestData);
    },
    function* (response) {
      const { modal } = yield select(signInOutSelector);

      yield put(signInOutSlice.actions.signinOk(response));

      /** stay on the same page if login via modal */
      let redirectUrl = '/';

      if (response.RedirectUrl) {
        redirectUrl = response.RedirectUrl;
      }
      const returnurlParam = getSearchReturnUrl();
      if (returnurlParam && !modal) {
        redirectUrl = returnurlParam;
      } else if (initialEmail === requestData.email) {
        /* for Support users who were redirected from /start or /plus/start */
        const userModelResponse: PromiseReturnType<typeof apiLoadUserModel> = yield call(apiLoadUserModel);

        if (userModelResponse.UserModel.UserTypeCode === Enums.common.UserTypeCode.Supporter) {
          redirectUrl = from ? `${ROUTE_CUSTOMER_TYPE}?from=${from}` : ROUTE_CUSTOMER_TYPE;
        }
      }

      if (modal) {
        /** if there is redirect path - redirect, otherwise load a user data */
        if (redirectUrl !== '/') {
          setLocalStorage('checkExpiredCampaignsModal', true);
          safeRedirect(redirectUrl);
        } else {
          yield put(currentUserSlice.actions.getCurrentUserStart());
          yield put(commonSlice.actions.checkExpiredCampaignsStart());
        }
      } else {
        setLocalStorage('checkExpiredCampaignsModal', true);
        safeRedirect(redirectUrl);
      }

      trackPartnerEvent('ExistingLogin');
    },
    signInOutSlice.actions.signinError
  );
}

function* signUpSaga({ payload }: PayloadAction<SignUpRequestType>): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiAccountSignup, payload);
    },
    function* (response) {
      const { modal } = yield select(signInOutSelector);

      yield put(signInOutSlice.actions.signUpOk(response));

      trackPartnerEvent('Lead');

      if (modal) {
        if (response.RedirectUrl) {
          safeRedirect(response.RedirectUrl);
        } else {
          yield put(currentUserSlice.actions.getCurrentUserStart());
        }
      } else {
        const returnurlParam = getSearchReturnUrl();

        safeRedirect(returnurlParam || ROUTE_MY_ACCOUNT_PAGE);
      }
    },
    signInOutSlice.actions.signUpError
  );
}

function* signOutSaga({ payload }: PayloadAction<string | undefined>): Generator {
  yield httpResponseCheckSaga(
    function* () {
      anonymizeFullstoryIdentity();
      return yield call(apiSignOut, getSearchReturnUrl());
    },
    function* ({ RedirectUrl }) {
      safeRedirect(payload || RedirectUrl || ROUTE_SIGNIN_PATH);

      yield 1;

      /** use this in case of soft SPA redirect */
      // eventPublishAuthChanged(null);
      // yield put(GLOBAL_CLEAR_STORE_ACTION);
    },
    signInOutSlice.actions.signOutError
  );
}

export function* signInOutSagas(): Generator {
  yield takeLatest(signInOutSlice.actions.signinStart.type, signInSaga);
  yield takeLatest(signInOutSlice.actions.signUpStart.type, signUpSaga);
  yield takeLatest(signInOutSlice.actions.signOutStart.type, signOutSaga);
}
